/**
 * Offline pack (GET /sync/pack): the sites assigned to this reporter plus
 * recent ones, the template versions resolved for them, carried-forward
 * findings and the reporter's own DRAFT/RETURNED surveys. A phone never
 * holds all sites (ADR-0020). This file is the only place that maps the
 * pack's wire shape to the device models.
 */
import { grantCovers, toIsoDate, type ServiceLine } from '@sss/core';
import { daysToFollowUp } from '@sss/workflow';
import type { ApiClient } from '../api/client';
import type {
  CarriedFinding,
  MyWork,
  MyWorkDueSite,
  PackCustomer,
  PackPerson,
  PackSite,
  PackSiteSettings,
  PackTemplateVersion,
  ServerPackSurvey,
  ServerSyncPack,
  SiteEnrolmentInfo,
  SyncPack,
  TemplateVersionRow,
} from '../api/types';
import type { OfflineStorage } from '../offline/types';

export const PACK_REFRESH_MS = 30 * 60_000;
export const PACK_PEOPLE_META = 'pack.people';
export const PACK_SETTINGS_META = 'pack.deviceSettings';

/** Today's date on this device (local calendar day), as an ISO date. */
export function todayLocalIso(now = new Date()): string {
  return toIsoDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}

/** Days to follow-up from today (ADR-0014: one definition, @sss/workflow); null when not tracked. */
export function daysToFollowUpOf(e: Pick<SiteEnrolmentInfo, 'nextDueDate'>, today = todayLocalIso()): number | null {
  return e.nextDueDate ? daysToFollowUp(e.nextDueDate, today) : null;
}

/** The site with every enrolment's days to follow-up worked out for today. */
export function withCurrentDue<T extends Pick<PackSite, 'enrolments'>>(site: T, today = todayLocalIso()): T {
  if (!site.enrolments?.length) return site;
  return { ...site, enrolments: site.enrolments.map((e) => ({ ...e, daysToFollowUp: daysToFollowUpOf(e, today) })) };
}

/** A cached /dashboard/my-work answer, with due days recomputed for today (used offline). */
export function withCurrentDueWork(w: MyWork, today = todayLocalIso()): MyWork {
  if (!w.reporter) return w;
  return { ...w, today, reporter: { ...w.reporter, dueSites: w.reporter.dueSites.map((d) => ({ ...d, daysToFollowUp: daysToFollowUpOf(d, today) })) } };
}

/** Map the wire pack to what the device stores. Due dates come from the pack (ADR-0014). */
export function fromServerPack(raw: ServerSyncPack, previous: Map<string, PackSite> = new Map()): SyncPack {
  const customers = new Map<string, PackCustomer>();
  const sites: PackSite[] = (raw.sites ?? []).map((s) => {
    customers.set(s.customerId, { id: s.customerId, code: s.customerCode, name: s.customerName });
    return {
      id: s.id,
      code: s.code,
      name: s.name,
      customerId: s.customerId,
      customerName: s.customerName,
      customerCode: s.customerCode,
      orgPath: s.orgPath,
      regionCode: s.regionCode,
      countryCode: s.countryCode,
      branchCode: s.branchCode,
      serviceLine: s.serviceLine,
      address: s.address,
      lat: s.lat,
      lng: s.lng,
      status: 'ACTIVE',
      templates: s.templates ?? {},
      // An older API without enrolments in the pack: keep what the device knew.
      enrolments: s.enrolments
        ? s.enrolments.map((e) => ({ surveyType: e.surveyType, nextDueDate: e.nextDueDate, lastSurveyDate: e.lastSurveyDate }))
        : previous.get(s.id)?.enrolments,
      assigned: s.assigned,
      settings: s.settings,
    };
  });
  const templates: PackTemplateVersion[] = Object.entries(raw.templates ?? {}).map(([versionId, definition]) => ({
    versionId,
    templateId: definition.id,
    version: null,
    definition,
  }));
  const carriedFindings: CarriedFinding[] = Object.values(raw.carriedFindings ?? {})
    .flat()
    .map((f) => ({
      id: f.id,
      siteId: f.site_id,
      humanId: f.human_id,
      controlCode: f.control_code,
      originCode: f.origin_code,
      severity: f.severity,
      status: f.status,
      dueDate: f.due_date,
      customerObservation: f.customer_observation,
      legacyText: f.legacy_text,
    }));
  return { serverTime: raw.serverTime ?? new Date().toISOString(), sites, customers: [...customers.values()], templates, carriedFindings, people: raw.people ?? [] };
}

/**
 * The strictest device settings across the pack's sites (ADR-0021, ADR-0023):
 * one phone may hold sites from countries with different rules, and the app
 * lock and offline sign-in limit protect all of them at once.
 */
export function strictestDeviceSettings(sites: Pick<PackSite, 'settings'>[]): Pick<PackSiteSettings, 'appLockMinutes' | 'offlineReauthDays'> | null {
  const all = sites.map((s) => s.settings).filter((x): x is PackSiteSettings => !!x);
  if (all.length === 0) return null;
  return {
    appLockMinutes: Math.min(...all.map((x) => x.appLockMinutes)),
    offlineReauthDays: Math.min(...all.map((x) => x.offlineReauthDays)),
  };
}

/** /me settings tightened by the pack's (never loosened). */
export function effectiveDeviceSettings<T extends { appLockMinutes: number; offlineReauthDays: number }>(
  me: T,
  pack: Pick<PackSiteSettings, 'appLockMinutes' | 'offlineReauthDays'> | null | undefined,
): T {
  if (!pack) return me;
  return { ...me, appLockMinutes: Math.min(me.appLockMinutes ?? pack.appLockMinutes, pack.appLockMinutes), offlineReauthDays: Math.min(me.offlineReauthDays ?? pack.offlineReauthDays, pack.offlineReauthDays) };
}

/**
 * People a person field may offer at a site (offline): holders of one of the
 * field's roles (any role when the field has no filter) in a scope that
 * covers the site.
 */
export function eligiblePeople(
  people: PackPerson[],
  site: Pick<PackSite, 'id' | 'orgPath' | 'customerId' | 'serviceLine'> | undefined,
  roleFilter: string[] | undefined,
): PackPerson[] {
  const roles = roleFilter?.length ? new Set(roleFilter) : null;
  const resource = site ? { path: site.orgPath, customerId: site.customerId, siteId: site.id, serviceLine: (site.serviceLine ?? null) as ServiceLine | null } : null;
  return people
    .filter((p) => p.grants.some((g) => (!roles || roles.has(g.roleId)) && (!resource || !resource.path || grantCovers({ ...g, serviceLine: (g.serviceLine ?? null) as ServiceLine | null }, resource))))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface DownloadPackOptions {
  /**
   * Bring the reporter's server-side drafts / returned surveys onto the device
   * (DraftService.reconcileServerSurveys). Called with every pack that lists
   * them, even an empty list, which says what the server no longer has.
   */
  onSurveys?: (surveys: ServerPackSurvey[], pack: SyncPack) => Promise<void>;
  /** Draft ids on this device; the server says which were cancelled or reassigned. */
  known?: string[];
  onWithdrawn?: (withdrawn: NonNullable<ServerSyncPack['withdrawn']>) => Promise<void>;
}

export async function downloadPack(api: ApiClient, storage: OfflineStorage, opts: DownloadPackOptions = {}): Promise<SyncPack> {
  const known = (opts.known ?? []).slice(0, 200);
  const raw = await api.get<ServerSyncPack>(`/sync/pack${known.length ? `?known=${known.join(',')}` : ''}`);
  const previous = new Map((await storage.listSites()).map((s) => [s.id, s]));
  const pack = fromServerPack(raw, previous);
  await storage.replacePack(pack);
  await storage.setMeta(PACK_PEOPLE_META, pack.people ?? []);
  await storage.setMeta(PACK_SETTINGS_META, strictestDeviceSettings(pack.sites));
  if (opts.onSurveys && raw.surveys) await opts.onSurveys(raw.surveys, pack);
  if (opts.onWithdrawn && raw.withdrawn?.length) await opts.onWithdrawn(raw.withdrawn);
  return pack;
}

export async function packAge(storage: OfflineStorage, now = Date.now()): Promise<number | null> {
  const at = await storage.getMeta<number>('pack.downloadedAt');
  return at ? now - at : null;
}

/**
 * Fresher due dates from /dashboard/my-work onto the device's sites (the pack
 * refreshes every 30 minutes). my-work lists the reporter's assigned sites
 * due within 90 days, sorted by days to follow-up; an assigned site missing
 * from it whose stored date says it should be there (a survey was just
 * confirmed, the schedule was changed) is stale, and its date is cleared
 * until the next pack brings the new one.
 */
export async function mergeDueSites(storage: OfflineStorage, due: MyWorkDueSite[], today = todayLocalIso(), windowDays = 90): Promise<void> {
  const byKey = new Map(due.map((d) => [`${d.siteId}|${d.surveyType}`, d]));
  const types = new Set(due.map((d) => d.surveyType).concat('SSS'));
  // The list may be capped: only below the last listed value is absence proof.
  const listedUpTo = due.length ? Math.max(...due.map((d) => d.daysToFollowUp ?? -Infinity)) : windowDays;
  const strict = due.length > 0;
  const updated: PackSite[] = [];
  for (const site of await storage.listSites()) {
    let changed = false;
    const enrolments: SiteEnrolmentInfo[] = [...(site.enrolments ?? [])];
    for (const d of due.filter((x) => x.siteId === site.id)) {
      const i = enrolments.findIndex((e) => e.surveyType === d.surveyType);
      const next = { surveyType: d.surveyType, nextDueDate: d.nextDueDate, lastSurveyDate: d.lastSurveyDate };
      if (i < 0) enrolments.push(next);
      else if (enrolments[i]!.nextDueDate !== d.nextDueDate || enrolments[i]!.lastSurveyDate !== d.lastSurveyDate) enrolments[i] = { ...enrolments[i]!, ...next };
      else continue;
      changed = true;
    }
    if (site.assigned) {
      for (let i = 0; i < enrolments.length; i++) {
        const e = enrolments[i]!;
        if (!types.has(e.surveyType) || byKey.has(`${site.id}|${e.surveyType}`) || !e.nextDueDate) continue;
        const days = daysToFollowUpOf(e, today)!;
        if (strict ? days < listedUpTo : days <= windowDays) {
          enrolments[i] = { ...e, nextDueDate: null, daysToFollowUp: null };
          changed = true;
        }
      }
    }
    if (changed) updated.push({ ...site, enrolments });
  }
  if (updated.length) await storage.putSites(updated);
}

export function fromTemplateVersionRow(r: TemplateVersionRow): PackTemplateVersion {
  return { versionId: r.id, templateId: r.template_id, version: r.version, definition: r.definition };
}

/** The template version by id: from the device, or fetched and cached. */
export async function getTemplateVersion(api: ApiClient, storage: OfflineStorage, versionId: string): Promise<PackTemplateVersion> {
  const local = await storage.getTemplateVersion(versionId);
  if (local) return local;
  const t = fromTemplateVersionRow(await api.get<TemplateVersionRow>(`/template-versions/${encodeURIComponent(versionId)}`));
  await storage.putTemplateVersion(t);
  return t;
}

/**
 * The template version for a site: from the pack, or resolved online and
 * cached so the survey can continue offline.
 */
export async function resolveTemplateForSite(
  api: ApiClient,
  storage: OfflineStorage,
  siteId: string,
  surveyType: string,
): Promise<PackTemplateVersion | null> {
  const site = await storage.getSite(siteId);
  const versionId = site?.templates?.[surveyType];
  if (versionId) {
    const cached = await storage.getTemplateVersion(versionId);
    if (cached) return cached;
  }
  try {
    const t = fromTemplateVersionRow(
      await api.get<TemplateVersionRow>(`/templates/resolve?siteId=${encodeURIComponent(siteId)}&surveyType=${encodeURIComponent(surveyType)}`),
    );
    await storage.putTemplateVersion(t);
    return t;
  } catch {
    return null;
  }
}
